import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '@/config';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Search, Calendar, Clock, Phone, RefreshCw } from 'lucide-react';

export const FollowUpManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFollowUps = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/followups`);
      setFollowUps(response.data || []);
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowUps();

    // Refresh every 10 seconds for real-time updates
    const interval = setInterval(fetchFollowUps, 10000);
    return () => clearInterval(interval);
  }, []);

  // Calculate UI status based on follow-up date vs today
  const getUIStatus = (followupDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fDate = new Date(followupDate);
    fDate.setHours(0, 0, 0, 0);

    if (fDate.getTime() === today.getTime()) return 'today';
    if (fDate.getTime() < today.getTime()) return 'overdue';
    return 'upcoming';
  };

  // Add UI status to each follow-up
  const followUpsWithStatus = followUps.map(f => ({
    ...f,
    uiStatus: getUIStatus(f.date)
  }));

  const filteredFollowUps = followUpsWithStatus.filter(followUp => {
    const patientName = (followUp.patientName || followUp.patient_name || '').toLowerCase();
    const matchesSearch = patientName.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || followUp.uiStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      today: { variant: 'default', label: 'Today' },
      upcoming: { variant: 'secondary', label: 'Upcoming' },
      overdue: { variant: 'destructive', label: 'Overdue' }
    };
    const config = statusConfig[status] || statusConfig.upcoming;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const todayCount = followUpsWithStatus.filter(f => f.uiStatus === 'today').length;
  const upcomingCount = followUpsWithStatus.filter(f => f.uiStatus === 'upcoming').length;
  const overdueCount = followUpsWithStatus.filter(f => f.uiStatus === 'overdue').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 w-full">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">Follow-up Management</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Track and manage patient follow-up appointments</p>
        </div>
        {/* Mobile Icon-only Refresh Button */}
        <Button
          variant="outline"
          size="icon"
          onClick={fetchFollowUps}
          disabled={loading}
          className="sm:hidden h-9 w-9 flex-shrink-0"
          aria-label="Refresh follow-ups"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        {/* Desktop Refresh Button */}
        <Button
          variant="outline"
          onClick={fetchFollowUps}
          disabled={loading}
          className="hidden sm:inline-flex h-10 gap-1.5"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards (2 columns on mobile) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-6">
        <Card className="shadow-card min-w-0">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-1.5">
              <div className="min-w-0">
                <p className="text-[11px] sm:text-sm text-muted-foreground mb-0.5 sm:mb-1 truncate">Today's Follow-ups</p>
                <p className="text-xl sm:text-3xl font-bold font-serif text-primary">{todayCount}</p>
              </div>
              <div className="bg-primary/10 p-2 sm:p-3 rounded-lg flex-shrink-0">
                <Clock className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card min-w-0">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-1.5">
              <div className="min-w-0">
                <p className="text-[11px] sm:text-sm text-muted-foreground mb-0.5 sm:mb-1 truncate">Upcoming</p>
                <p className="text-xl sm:text-3xl font-bold font-serif">{upcomingCount}</p>
              </div>
              <div className="bg-muted p-2 sm:p-3 rounded-lg flex-shrink-0">
                <Calendar className="h-4 w-4 sm:h-6 sm:w-6 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card border-destructive/50 min-w-0">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-1.5">
              <div className="min-w-0">
                <p className="text-[11px] sm:text-sm text-muted-foreground mb-0.5 sm:mb-1 truncate">Overdue</p>
                <p className="text-xl sm:text-3xl font-bold font-serif text-destructive">{overdueCount}</p>
              </div>
              <div className="bg-destructive/10 p-2 sm:p-3 rounded-lg flex-shrink-0">
                <Calendar className="h-4 w-4 sm:h-6 sm:w-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by patient name..."
                className="pl-10 h-11 text-sm sm:text-base"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Follow-ups</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Follow-ups List */}
      <Card className="shadow-card">
        <CardHeader className="border-b border-border py-4 px-4 sm:px-6">
          <CardTitle className="font-serif text-xl">Follow-up Schedule ({filteredFollowUps.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card List (< md) */}
          <div className="block md:hidden divide-y divide-border">
            {filteredFollowUps.length > 0 ? (
              filteredFollowUps.map((followUp) => (
                <div key={followUp.id} className={`p-4 space-y-3 ${followUp.uiStatus === 'overdue' ? 'bg-destructive/5' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-slate-900 text-base">
                        {followUp.patientName || followUp.patient_name}
                      </div>
                      {followUp.phone && followUp.phone !== 'N/A' && (
                        <a href={`tel:${followUp.phone}`} className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5">
                          <Phone className="h-3 w-3" />
                          {followUp.phone}
                        </a>
                      )}
                    </div>
                    <div>{getStatusBadge(followUp.uiStatus)}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs bg-muted/40 p-2.5 rounded-lg">
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Follow-up Date</span>
                      <span className="font-medium text-slate-800 flex items-center gap-1 mt-0.5">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        {followUp.date}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Visit Date</span>
                      <span className="font-medium text-slate-700 mt-0.5 block">{followUp.visit_date || 'N/A'}</span>
                    </div>
                  </div>

                  {followUp.notes && followUp.notes !== 'N/A' && (
                    <p className="text-xs text-muted-foreground bg-muted/20 px-2.5 py-1.5 rounded">
                      <span className="font-medium text-slate-700">Notes: </span>{followUp.notes}
                    </p>
                  )}

                  <Button variant="outline" className="w-full h-11 text-sm font-medium" asChild>
                    <Link to={`/admin/patients/${followUp.patient_id}`}>View Patient Profile</Link>
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm p-4">
                No follow-ups found.
              </div>
            )}
          </div>

          {/* Desktop Table (md+) */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Visit Date</TableHead>
                  <TableHead>Follow-up Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFollowUps.length > 0 ? (
                  filteredFollowUps.map((followUp) => (
                    <TableRow key={followUp.id} className={followUp.uiStatus === 'overdue' ? 'bg-destructive/5' : ''}>
                      <TableCell className="font-medium">{followUp.patientName || followUp.patient_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {followUp.phone || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>{followUp.notes || 'N/A'}</TableCell>
                      <TableCell>{followUp.visit_date || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {followUp.date}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(followUp.uiStatus)}</TableCell>
                      <TableCell className="text-center">
                        <Button variant="outline" size="sm" asChild className="justify-center">
                          <Link to={`/admin/patients/${followUp.patient_id}`}>View Patient</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No follow-ups found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FollowUpManagement;
